import { EpubViewerInstance } from "src/app/services/instance/epub/epub-instance-manager.service";
import { ILanguage } from "../interfaces";
import { AsgardDocBase } from "./asgard-doc-base";
import { ReadingAtom } from "../reading-utils/interfaces";
import { ReadingMode, ReadingUnitType, SpeakingJobType } from "../reading-utils/enums";
import { HighlightingTypes } from "../pdf-utils/enums";
import { TextAtom } from "src/lib/cheshire-cat/models/text-atom.model";
import {
    IContentBlock,
    IContentBlockTree,
    IKeyboardEngineEvent,
    IMouseEngineEvent,
    INavigationIntentEngineEvent,
    IPointerEngineEvent,
    IReaderDocument,
    IReaderView,
    IReaderViewEngineEvent,
    IReaderViewTransformEngineEvent,
    ITransformData,
    IVisiblePage,
} from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { EpubTextIterator } from "../epub-utils/epub-text-iterator";
import { SettingsService } from "src/app/services/settings/settings.service";
import { ReadingService } from "src/app/services/text/reading.service";
import { ReadingActions } from "../enums";
import { ToastrDialogService } from "src/app/services/toastr-dialog.service";
import { ToastrActionEnum, ToastrStringsEnum } from "src/app/utils/enums";
import { ISelectionChangedEngineEvent } from "@colibrio/colibrio-reader-framework/colibrio-readingsystem-base";
import { CurrentUiStoreService } from "src/app/services/ui/current-ui-store.service";
import { AsgardFeatureNames } from "src/models/AsgardFeatures";
import { NgZone } from "@angular/core";
import { ReadingModesService } from "src/app/services/reading-modes.service";
import { EpubReadingHighlighter } from "../epub-utils/epub-reading-highlighter";
import { BehaviorSubject, debounceTime, filter, first, Observable, Subject, Subscription, switchMap } from "rxjs";
import { IAutoSaveDocChanges } from "src/app/services/autosave/autosave.interface";
import { IEpubAnnotationService, IPersistedEpubAnnotation } from "src/app/services/epub/base-epub-annotation.service";
import { AutosaveManagerService } from "src/app/services/autosave/autosave-manager.service";
import { TabService } from "src/app/services/tab.service";
import { AsgardTab } from "../asgard-tab";
import { TextMapWorkerWrapper } from "../epub-utils/text-map-worker-wrapper";
import { TextProcessorWorkerWrapper } from "../epub-utils/text-processor-worker-wrapper";
import { TranslateService } from "@ngx-translate/core";

export class AsgardDocEpub extends AsgardDocBase {
    get isDocumentReady(): boolean { return this.epubViewerInstance !== null }
    public epubViewerInstance: EpubViewerInstance | null = null;
    public isDocumentReady$ = new BehaviorSubject<boolean>(false);
    private selectionText: string | null = null;
    private textIterator: EpubTextIterator;
    private readingService: ReadingService;
    private toastrService: ToastrDialogService;
    private processingIndexes: number[] = [];
    private processingTextMapKey = 'ProcessingTextMapKey';
    private isMarkersActive = false;
    private ngZone: NgZone; // for click and read
    private readingModeService: ReadingModesService; // for click and read
    private readingHighlighter: EpubReadingHighlighter | undefined; // for double highlighting
    private isProcessingNextItemToRead = false;
    private allAnnotationServices: IEpubAnnotationService[];
    private autosave: AutosaveManagerService;
    private activeTab$: Observable<AsgardTab | null>;
    private textMapWorkerWrapper = new TextMapWorkerWrapper();
    private textProcessorWorkerWrapper = new TextProcessorWorkerWrapper();
    private subscriptions: Subscription = new Subscription();
    private isReadingLink = false;
    private translate: TranslateService;

    private activeTransformWhileSelectingOnZoom: ITransformData | null = null;

    constructor(public readonly documentId: string) {
        super(documentId);
    }

    // used after colibrio and document manager have loaded everything
    async onInstanceReady(settingsService: SettingsService, readingService: ReadingService, toastrService: ToastrDialogService, uiStore: CurrentUiStoreService, ngZone: NgZone, readingModeService: ReadingModesService, allAnnotationServices: IEpubAnnotationService[], autosave: AutosaveManagerService, tabService: TabService, translate: TranslateService): Promise<void> {
        this.textIterator = new EpubTextIterator();
        this.readingService = readingService;
        this.toastrService = toastrService;
        this.ngZone = ngZone;
        this.readingModeService = readingModeService;
        this.allAnnotationServices = allAnnotationServices;
        this.autosave = autosave;
        this.translate = translate;
        this.activeTab$ = tabService.activeTabObservable();

        if (this.epubViewerInstance) {
            this.readingHighlighter = new EpubReadingHighlighter(this.epubViewerInstance.readerView);
        }

        this.subscriptions.add(
            settingsService.getSettingsObservable().subscribe((settings) => {
                if (this.textIterator.readingUnit !== settings.readingUnit) {
                    // in pdf, when reading unit changes
                    // reading atoms are emptied
                    this.setReadingItem([]);
                    this.readingHighlighter?.destroyAllHighlights();
                }
                this.textIterator.readingUnit = settings.readingUnit;
                // for double highlighting
                this.readingHighlighter?.settingsChanged(settings.currentWordColor, settings.readingUnitColor);
            })
        );

        this.subToState(uiStore);
        await this.autosave.checkForSavedPageAndReadingPostion(this.documentId);
        this.isDocumentReady$.next(true);
    }

    onInstanceDestroy(): void {
        this.textMapWorkerWrapper.terminate();
        this.textProcessorWorkerWrapper.terminate();
        this.subscriptions.unsubscribe();
        this.toastrService.hideToastr(this.processingTextMapKey);
    }

    // from Base class
    async setDocumentOptins(lngMpr: (...args: any) => ILanguage, viewer: EpubViewerInstance): Promise<void> {
        super.setDocumentOptins(lngMpr);
        this.epubViewerInstance = viewer;
        this.addEventHandles();
    }

    getShowedDocRect(): { x1: number; y1: number; x2: number; y2: number; } | null {
        console.log('TODO AsgardDocEpub-getShowedDocRect()');
        // can be retrieved from the visible pages
        return null;
    }

    doHighlight(_words: ReadingAtom[], _color: string, _itemType: HighlightingTypes, _exclusive?: boolean): Promise<void> {
        const location = this.textIterator.getLocation(_words);
        if (!location  || !this.readingHighlighter) {
            console.error('Could not create highlight, location not found. ', _words);
            return Promise.reject('Could not create highlight, location not found.');
        }

        this.readingHighlighter.createHighlight(location, _itemType);
        return Promise.resolve();
    }

    scrollToWord(_word: ReadingAtom): void {
        console.log('TODO AsgardDocEpub-scrollToWord()');
    }

    hasNextItemToRead(_itemType: ReadingUnitType): boolean {
        // NOTE, item type retrieved from settings in textIterator
        return this.textIterator.hasNextItem();
    }

    goToNextReadingItem(_itemType: ReadingUnitType): Promise<void> {
        this.isProcessingNextItemToRead = true;
        this.isReadingLink = false;
        // NOTE, item type retrieved from settings in textIterator
        // Promise because of base class
        return new Promise(async (res) => {
            this.readingHighlighter?.destroyAllHighlights();
            const atoms = this.textIterator.getNextItem();
            if (this.epubViewerInstance) {
                const readerView: IReaderView = this.epubViewerInstance.readerView;
                await this.textIterator.scrollToReadingAtom(atoms[0], readerView)
            }
            this.setReadingItem(atoms);
            this.highlightWord(atoms[0]);
            this.isProcessingNextItemToRead = false;
            res();
        });
    }

    getSpeakingJobType(): Promise<SpeakingJobType> {
        return Promise.resolve(this.isReadingLink ? SpeakingJobType.Link : SpeakingJobType.Document);
    }

    clearTextHighlight(force?: boolean, _clicked?: boolean): void {
        this.readingHighlighter?.destroyAllHighlights();
        super.clearTextHighlight(force, _clicked);
    }

    // for markers
    selectedText(): string {
        return this.selectionText ?? '';
    }

    getCurrentPageIdx(): number {
        return this.epubViewerInstance?.readerView.getPageProgressionTimeline()?.getVisibleTimelineRange().start.pageIndex ?? 0;
    }

    async getDocumentUnsavedChanged(): Promise<IAutoSaveDocChanges> {
        let data: IAutoSaveDocChanges | null = null;
        const pageIdx = this.getCurrentPageIdx();
        data = {
            unsavedChanges: JSON.stringify(await this.getAnnotations()),
            documentHash: this.docHash as string,
            fileName: this.epubViewerInstance?.epubFile.name ?? null,
            readingItemIdx: this.getReadingPostition(),
            readingItemPageIdx: pageIdx,
            pageIdx: pageIdx
        };
        return data;
    }

    async applyDocumentChanges(_docHash: string, _changes: string): Promise<void> {
        this.docHash = _docHash;
        const data = JSON.parse(_changes) as IPersistedEpubAnnotation[];
        if (!data) return;

        // NOTE 13 NOV 2025
        // when multiple epubs are restored at once
        // only the last tab will be visible and only those annotations will be applied
        // the epub tabs before that will not have their annotations applied
        // so to fix that, we need to apply the annotations when the tab becomes visible (active)
        // (isDocumentReady$ and activeTab$ can not be combined because activeTab$ is undefined untill isDocumentReady$ is true -ich)
        // same for lastReadingPositionFounded
        this.isDocumentReady$.pipe(
            filter(Boolean),
            first(),
            switchMap(() =>
                this.activeTab$.pipe(
                    filter(tab => tab?.id === this.documentId),
                    first()
                )
            )
        ).subscribe(() => {
            this.epubViewerInstance?.readerView.refresh(true);
            this.applyAnnotations(data);
        });
    }

    async lastReadingPositionFounded(_pageIdx: number | null, _readingItemIdx: number | null, _readingItemPageIdx: number | null, _itemType: ReadingUnitType) {
        if (_pageIdx) {
            // see NOTE 13 NOV 2025
            // applyDocumentChanges
            this.isDocumentReady$.pipe(
                filter(Boolean),
                first(),
                switchMap(() =>
                    this.activeTab$.pipe(
                        filter(tab => tab?.id === this.documentId),
                        first()
                    )
                )
            ).subscribe(async _ => {
                const handler = async () => {
                    if (!this.epubViewerInstance) return;
                    const ppt = this.epubViewerInstance.readerView.getPageProgressionTimeline();
                    if (!ppt) return;

                    this.epubViewerInstance.readerView.removeEngineEventListener('pageProgressionTimelineVisibleRangeChanged', handler);
                    const locator = await ppt.fetchLocatorFromPageIndex(_pageIdx);
                    await this.epubViewerInstance.readerView.goTo(locator);
                }
                this.epubViewerInstance?.readerView.addEngineEventListener('pageProgressionTimelineVisibleRangeChanged', handler);
            });
        }
    }

    public async getAnnotations(): Promise<IPersistedEpubAnnotation[]> {
        const annotations: IPersistedEpubAnnotation[] = [];
        for (const svc of this.allAnnotationServices) {
            annotations.push(...(await svc.exportAnnotations(this.documentId)));
        }
        return annotations;
    }

    public applyAnnotations(annotations: IPersistedEpubAnnotation[]) {
        for (const a of annotations) {
            const svc = this.allAnnotationServices.find(s => s.LAYER_NAME === a.layer);
            svc?.importAnnotation(a);
        }
    }

    // state
    private subToState(uiStore: CurrentUiStoreService): void {
        this.subscriptions.add(
            uiStore.getUIState().subscribe(e => {
                if (e) {
                    // keep track if there is a marker or the eraser active
                    this.isMarkersActive = e[AsgardFeatureNames.MARKERS].active;
                }
            })
        );
    }

    // reading
    private runReadingServiceAction(action: ReadingActions): void {
        this.ngZone.run(() => this.readingService.runAction(action));
    }

    // event handlers from colibrio
    private addEventHandles(): void {
        if (!this.epubViewerInstance) {
            console.error('could not add events. Instance does not exist.');
            return;
        }

        const readerView: IReaderView = this.epubViewerInstance.readerView;
        // Fired when the current selection has changed in content documents.
        readerView?.addEngineEventListener('selectionChanged', (event: ISelectionChangedEngineEvent) => {
            this.selectionText = event.selectionText;

            // if the user has zoomed in AND is trying to select text, save the activeTransform to prevent panning while selecting
            // (my opinion, feels hackish, but changing the readerview options to block panning refreshes the readerView and event.preventDefault doesn't work!!)
            const activeTransform = readerView?.getTransformManager()?.getActiveTransform();
            if (activeTransform && activeTransform.scale > 1 && event.selectionText !== '' && event.selectionText !== null) {
                this.activeTransformWhileSelectingOnZoom = activeTransform;
            } else {
                this.activeTransformWhileSelectingOnZoom = null;
            }
        })

        // This event fires when a ReaderView's active transform has changed. 
        // This happens when performing a pan-zoom gesture, or when calling transform methods on the ReaderViewTransformManager such as zoomToEventPosition().
        readerView?.addEngineEventListener('activeTransformChanged', (event: IReaderViewTransformEngineEvent) => {
            // if the user zoomed back out, forget what the active transform was while zoomed in
            if ((event.transform?.scale ?? 1) === 1 || !event.transform?.userGestureActive) {
                this.activeTransformWhileSelectingOnZoom = null;
            } else if (this.activeTransformWhileSelectingOnZoom) { // userGestureActive is true when pan-zooming
                readerView?.getTransformManager()?.applyTransform(this.activeTransformWhileSelectingOnZoom, { durationMs: 0 });
            }
        });

        // Event fired when a key is released.
        readerView?.addEngineEventListener('keyup', (event: IKeyboardEngineEvent) => {
            if (event.key === 'ArrowRight') {
                if (readerView?.canPerformNext() ?? false) readerView?.next();
            } else if (event.key === 'ArrowLeft') {
                if (readerView?.canPerformPrevious() ?? false) readerView?.previous();
            }
        });

        // Event fired when a click occurs in a page.
        readerView?.addEngineEventListener('click', async (click: IMouseEngineEvent) => {
            this.isReadingLink = false;
            this.readingService.runAction(ReadingActions.Stop);
            this.readingHighlighter?.destroyAllHighlights();
            // don't read when the markers are active
            // when text is selected, reading should stop
            // the new reading should not start
            if (!this.isMarkersActive && (this.selectionText === '' || this.selectionText === null)) {
                const items = await this.textIterator.getItemsByClick(click, readerView)
                this.setReadingItem(items);
                if (items.length > 0) {
                    // highlight the first word
                    this.highlightWord(items[0]);
                }

                if (this.readingModeService.readingMode === ReadingMode.ClickToRead) {
                    this.runReadingServiceAction(ReadingActions.PlayPause);
                }
            }
        });

        // Fired when the list of visible pages inside a ReaderView has changed.
        readerView.addEngineEventListener('visiblePagesChanged', async (visiblePagesChanged: IReaderViewEngineEvent) => {
            this.isReadingLink = false;
            this.readingService.runAction(ReadingActions.Stop);
            this.readingHighlighter?.destroyAllHighlights();
            // get the visible pages
            const visiblePages: IVisiblePage[] = visiblePagesChanged.readerView.getVisiblePages();
            this.autosave.updateDocumentPage(this.docHash, this.getCurrentPageIdx());
            // get the spine index of those visible pages
            const indexesInSpine = visiblePages.map((x) => x.toJSON().readerDocumentIndexInSpine);
            // check if there is already a textMap for the spine items of the visible pages
            // Use for...of instead of forEach to wait for the 'await'
            for (const index of indexesInSpine) {
                // is the textmap loaded?
                if (!this.textIterator.hasProcessed(index) && !this.processingIndexes.includes(index)) {
                    // if not, then retrieve the textMap
                    this.loadTextMap(index, true);
                }
            }
            // if the page changed within the same spine item, set the first reading item to the beginning of the page
            if (this.textIterator.hasProcessed(indexesInSpine[0])) {
                // set the reading unit if the last visible page was processed
                this.startReadingFirstItem(visiblePages[0]);
            }

            // if pages changes to a page that is already processed
            // and there are no more items in the queue to process
            // get the next item that was not processed yet?
            if (this.processingIndexes.length === 0) {
                const highestVisibleIndexInSpine = Math.max(...indexesInSpine);
                const nextIndex = this.epubViewerInstance?.extractedHTMLList?.find((x) => x.indexInSpine === highestVisibleIndexInSpine + 1 && !this.textIterator.hasProcessed(x.indexInSpine));
                if (nextIndex) {
                    this.loadTextMap(nextIndex.indexInSpine, false);
                }
            }
        });

        // only listen to the last pointer move, 500ms after the user stopped moving
        const pointerMove$ = new Subject<IReaderViewEngineEvent>();
        readerView.addEngineEventListener('pointermove', (pointermove: IPointerEngineEvent) => pointerMove$.next(pointermove));
        pointerMove$.pipe(debounceTime(500)).subscribe(async (pointermove: IPointerEngineEvent) => { // 500ms bcs Caliban
            // don't read when the markers are active
            // when text is selected, reading should stop
            // the new reading should not start
            if (!this.isMarkersActive && (this.selectionText === '' || this.selectionText === null)) {
                let items: ReadingAtom[] = [];
                switch (this.readingModeService.readingMode) {
                    case ReadingMode.Hover: break; // TODO
                    default:
                        // in other reading modes, first check if you hovered over a link
                        if (pointermove.target?.nodeData?.nodeName === 'a') {
                            items = await this.textIterator.getLinkItemsByHover(pointermove, readerView);
                        }
                }

                if (items && items.length > 0) {
                    this.readingService.runAction(ReadingActions.Stop);
                    this.readingHighlighter?.destroyAllHighlights();
                    this.setReadingItem(items);
                    this.highlightSentence(items);
                    this.isReadingLink = true;
                    this.runReadingServiceAction(ReadingActions.PlayPause);
                }
            }
        });

        readerView.addEngineEventListener('navigationIntent', (event: INavigationIntentEngineEvent) => {
            this.isReadingLink = false;
            this.readingService.runAction(ReadingActions.Stop);
            this.readingHighlighter?.destroyAllHighlights();

            if (!event.internalNavigation) {
                const url = event.locator.getSourceUrl();
                const confirmed = window.confirm(this.translate.instant('NAVIGATE_EXTERNAL', { url }));
                if (confirmed) window.open(url, '_blank', 'noopener');
            }
        });
    }

    // cheshire cat
    private async loadTextMap(spineItemIndex: number, showToastr: boolean): Promise<void> {
        // show the spinner if processing the visible pages
        // TODO check the active tab!
        if (showToastr) {
            this.toastrService.showToastrByType(ToastrActionEnum.Info, ToastrStringsEnum.ProcessingTextMap, true, this.processingTextMapKey);
        }


        this.processingIndexes.push(spineItemIndex);
        console.log('loadTextMap for spine item index: ', spineItemIndex);
        if (this.epubViewerInstance === null) {
            console.error('Could not create textMap, instance does not exist.');
            return;
        }

        const extractedHTML = this.epubViewerInstance.extractedHTMLList.find((x) => x.indexInSpine === spineItemIndex);
        if (!extractedHTML) {
            console.error('Could not create textMap, index not found. ', spineItemIndex);
            return;
        }

        // let cheshire cat lose!
        // TODO, there is no need to process the html if there is no actual text on the page
        // but how to determine that?
        // Use Web Worker for processing so main thread would not be blocked for page changes
        this.textMapWorkerWrapper.postMessage({ spineItemIndex, extractedHTML }, (event) => {
            const { textMap, error } = event.data;
            if (error) {
                console.error(error);
                return;
            }
            // textMap can be undefined if ReplaySubject had to many and it was ignored
            console.log(`doc - i: ${spineItemIndex}, textMap: `, textMap);
            if (textMap) {
                this.processTextMap(spineItemIndex, textMap);
            } else {
                // remove the index from the processing list
                this.processingIndexes = this.processingIndexes.filter((x) => x !== spineItemIndex);
            }
        });
    }

    // link textMap words to content blocks
    // content blocks appear to be <p>
    // unless that it contains an <br/>
    // in that case the <p> appears split up
    private async processTextMap(spineItemIndex: number, textMap: TextAtom[]): Promise<void> {
        if (!this.epubViewerInstance) {
            console.error('could not process text map. Instance does not exist.');
            return;
        }

        // textMap items all have an id of 0 when returned, so give them an id for easy finding
        textMap.forEach((item, i) => {
            item.itemId = i;
        });

        // get the spine
        const readerPublication = this.epubViewerInstance.readerPublication;
        const readerDocumentSpine: IReaderDocument[] = readerPublication.getSpine();
        const readerDocument: IReaderDocument | undefined = readerDocumentSpine.find((x) => x.getIndexInSpine() === spineItemIndex);

        if (!readerDocument) {
            console.error('Could not process text map. Could not find item in spine at index: ', spineItemIndex);
            return;
        }

        // fetch all the content blocks and check which text atoms are in which content blocks
        const tree: IContentBlockTree = await readerDocument.fetchContentBlockTree();
        const contentBlocks: IContentBlock[] = tree.getContentBlocks();
        const processedBlocks = await this.textProcessorWorkerWrapper.postMessage(contentBlocks, textMap);

        this.textIterator.addProcessedContentBlocks(spineItemIndex, textMap, processedBlocks);
        // remove the index from the processing list
        this.processingIndexes = this.processingIndexes.filter((x) => x !== spineItemIndex);

        // check what is done processing
        const readerView: IReaderView = this.epubViewerInstance.readerView;
        const visiblePages: IVisiblePage[] = readerView.getVisiblePages();
        const visiblePagesIndexInSpine = visiblePages.map((x) => x.toJSON().readerDocumentIndexInSpine);

        // if the visible pages are done processing 
        // and the item just processed is in the visible pages
        if ((this.processingIndexes.length === 0 || visiblePagesIndexInSpine.every((x) => !this.processingIndexes.includes(x)))
            && visiblePagesIndexInSpine.includes(spineItemIndex)
        ) {
            this.toastrService.hideToastr(this.processingTextMapKey);

            // set the reading unit if the last visible page was processed
            this.startReadingFirstItem(visiblePages[0]);

            // start processing the next spine element (if there is any)
            const nextIndex = this.epubViewerInstance?.extractedHTMLList?.find((x) => x.indexInSpine === (Math.max(...visiblePagesIndexInSpine) + 1));
            if (nextIndex && !this.processingIndexes.includes(nextIndex.indexInSpine) && !this.textIterator.hasProcessed(nextIndex.indexInSpine)) {
                this.loadTextMap(nextIndex.indexInSpine, false);
            }
        }
    }

    private async startReadingFirstItem(visiblePage: IVisiblePage): Promise<void> {
        // if the page didn't get turned while reading
        // use boolean because the reader status changes to stopped after every read job
        if (!this.isProcessingNextItemToRead) {
            const atoms = await this.textIterator.getFirstItem(visiblePage)
            // if there are no atoms, then there is no text to read
            if (atoms.length > 0) {
                this.setReadingItem(atoms);
                // highlight the first word
                this.highlightWord(atoms[0]);
            }
        }
    }

    private highlightWord(atom: ReadingAtom): void {
        const locationWord = this.textIterator.getLocation([atom]);
        if (locationWord) this.readingHighlighter?.createHighlight(locationWord, HighlightingTypes.Word);   
    }

    private highlightSentence(atoms: ReadingAtom[]): void {
        const locationSentence = this.textIterator.getLocation(atoms);
        if (locationSentence) this.readingHighlighter?.createHighlight(locationSentence, HighlightingTypes.Sentence);
    }
}